import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Users,
  Bell,
  ClipboardList,
  Wrench,
  Calendar as CalendarIcon,
  LogOut,
  Sparkles,
  ShieldCheck,
  Building2,
  Map as MapIcon,
  ReceiptText,
  UserCog,
  Boxes,
  Truck,
  ShoppingCart,
  CalendarClock,
  CheckCheck,
  AlertTriangle,
  PackageOpen,
  Clock3,
  X,
  Loader2,
  CreditCard
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AIAssistant } from "@/components/AIAssistant";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  {
    to: "/panel",
    label: "Panel",
    icon: LayoutDashboard,
    testid: "nav-dashboard",
    end: true,
    minPlan: "starter",
  },
  {
    to: "/avisos",
    label: "Avisos",
    icon: Bell,
    testid: "nav-avisos",
    minPlan: "starter",
  },
  {
    to: "/ordenes",
    label: "Órdenes de trabajo",
    icon: ClipboardList,
    testid: "nav-work-orders",
    minPlan: "starter",
  },
  {
    to: "/mapa",
    label: "Mapa Operativo",
    icon: MapIcon,
    testid: "nav-map",
    minPlan: "professional",
  },
  {
    to: "/clientes",
    label: "Clientes",
    icon: Users,
    testid: "nav-clients",
    minPlan: "starter",
  },
  {
    to: "/tecnicos",
    label: "Técnicos",
    icon: Wrench,
    testid: "nav-technicians",
    minPlan: "starter",
  },
  {
    to: "/calendario",
    label: "Calendario",
    icon: CalendarIcon,
    testid: "nav-calendar",
    minPlan: "starter",
  },
  {
    to: "/quotes",
    label: "Presupuestos",
    icon: ReceiptText,
    testid: "nav-quotes",
    minPlan: "starter",
  },
  {
    to: "/facturas",
    label: "Facturas",
    icon: ReceiptText,
    testid: "nav-invoices",
    minPlan: "starter",
  },
  {
    to: "/inventario",
    label: "Inventario",
    icon: Boxes,
    testid: "nav-inventory",
    minPlan: "professional",
  },
  {
    to: "/proveedores",
    label: "Proveedores",
    icon: Truck,
    testid: "nav-suppliers",
    roles: ["admin", "gerente", "oficina"],
    minPlan: "professional",
  },
  {
    to: "/compras",
    label: "Compras",
    icon: ShoppingCart,
    testid: "nav-purchases",
    roles: ["admin", "gerente", "oficina"],
    minPlan: "professional",
  },
  {
    to: "/auditoria",
    label: "Auditoría",
    icon: ShieldCheck,
    testid: "nav-audit",
    adminOnly: true,
    minPlan: "business",
  },
  {
    to: "/empresa",
    label: "Empresa",
    icon: Building2,
    testid: "nav-settings",
    adminOnly: true,
    minPlan: "starter",
  },
  {
    to: "/equipo",
    label: "Equipo y permisos",
    icon: UserCog,
    testid: "nav-team",
    adminOnly: true,
    minPlan: "starter",
  },
  {
    to: "/facturacion-navirox",
    label: "Planes y facturación",
    icon: CreditCard,
    testid: "nav-billing",
    adminOnly: true,
    alwaysVisible: true,
  },
];

const NOTIFICATION_ICONS = {
  invoice_overdue: ReceiptText,
  stock_low: PackageOpen,
  urgent_alert: AlertTriangle,
  work_order_today: CalendarIcon,
  work_order_assigned: ClipboardList,
  maintenance_due: CalendarClock,
};

function relativeDate(value) {
  if (!value) return "";

  const date = new Date(value);
  const seconds = Math.floor(
    (Date.now() - date.getTime()) / 1000
  );

  if (Number.isNaN(seconds)) return "";

  if (seconds < 60) return "Ahora";
  if (seconds < 3600) {
    return `Hace ${Math.floor(seconds / 60)} min`;
  }
  if (seconds < 86400) {
    return `Hace ${Math.floor(seconds / 3600)} h`;
  }

  return date.toLocaleDateString("es-ES");
}

function NotificationsButton({ navigate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({
    left: 280,
    bottom: 24,
  });

  const updatePanelPosition = () => {
    if (!buttonRef.current) return;

    const buttonRect =
      buttonRef.current.getBoundingClientRect();
    const panelWidth = Math.min(
      380,
      window.innerWidth - 32
    );

    let left = buttonRect.right + 14;

    if (left + panelWidth > window.innerWidth - 16) {
      left = Math.max(
        16,
        window.innerWidth - panelWidth - 16
      );
    }

    const bottom = Math.max(
      16,
      window.innerHeight - buttonRect.bottom
    );

    setPanelPosition({ left, bottom });
  };

  const loadNotifications = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnreadCount(Number(data.unread_count || 0));
    } catch (error) {
      if (!silent) {
        toast.error(
          formatApiError(error) ||
            "No se pudieron cargar las notificaciones."
        );
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    const timer = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 60_000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const closeOutside = (event) => {
      const clickedButton =
        containerRef.current?.contains(event.target);
      const clickedPanel =
        panelRef.current?.contains(event.target);

      if (!clickedButton && !clickedPanel) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOutside);

    return () => {
      document.removeEventListener(
        "mousedown",
        closeOutside
      );
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updatePanelPosition();

    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener(
      "scroll",
      updatePanelPosition,
      true
    );

    return () => {
      window.removeEventListener(
        "resize",
        updatePanelPosition
      );
      window.removeEventListener(
        "scroll",
        updatePanelPosition,
        true
      );
    };
  }, [open]);

  const openNotification = async (notification) => {
    if (!notification.read) {
      try {
        await api.post(
          `/notifications/${encodeURIComponent(
            notification.id
          )}/read`
        );

        setItems((current) =>
          current.map((item) =>
            item.id === notification.id
              ? { ...item, read: true }
              : item
          )
        );

        setUnreadCount((current) =>
          Math.max(current - 1, 0)
        );
      } catch {
        // La navegación sigue aunque falle el marcado.
      }
    }

    setOpen(false);
    navigate(notification.route || "/");
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");

      setItems((current) =>
        current.map((item) => ({
          ...item,
          read: true,
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          const nextOpen = !open;

          if (nextOpen) {
            updatePanelPosition();
            loadNotifications({ silent: true });
          }

          setOpen(nextOpen);
        }}
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#86868B] transition-colors hover:bg-[#F5F5F7] hover:text-[#0066CC]"
        aria-label="Notificaciones"
      >
        <Bell className="h-[18px] w-[18px]" />

        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              left: panelPosition.left,
              bottom: panelPosition.bottom,
              width: "min(380px, calc(100vw - 32px))",
            }}
            className="fixed z-[9999] overflow-hidden rounded-3xl border border-[#E8E8ED] bg-white shadow-[0_24px_80px_rgba(0,0,0,0.22)]"
          >
          <div className="flex items-center justify-between border-b border-[#E8E8ED] px-5 py-4">
            <div>
              <p className="font-display text-lg font-semibold text-[#1D1D1F]">
                Notificaciones
              </p>
              <p className="text-xs text-[#86868B]">
                {unreadCount
                  ? `${unreadCount} sin leer`
                  : "Todo al día"}
              </p>
            </div>

            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  title="Marcar todas como leídas"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-[#0066CC] hover:bg-[#0066CC]/10"
                >
                  <CheckCheck className="h-4 w-4" />
                </button>
              )}

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#F5F5F7]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[430px] overflow-y-auto p-2">
            {loading ? (
              <div className="flex min-h-40 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-[#0066CC]" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <Bell className="mx-auto h-8 w-8 text-[#B0B0B5]" />
                <p className="mt-3 text-sm font-medium text-[#1D1D1F]">
                  No tienes notificaciones
                </p>
                <p className="mt-1 text-xs text-[#86868B]">
                  Navirox te avisará aquí de lo importante.
                </p>
              </div>
            ) : (
              items.map((notification) => {
                const Icon =
                  NOTIFICATION_ICONS[
                    notification.type
                  ] || Bell;

                return (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() =>
                      openNotification(notification)
                    }
                    className={`flex w-full items-start gap-3 rounded-2xl p-3 text-left transition-colors hover:bg-[#F5F5F7] ${
                      notification.read
                        ? "opacity-65"
                        : "bg-[#F4F8FE]"
                    }`}
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        notification.priority === "high"
                          ? "bg-[#FFEBEA] text-[#D70015]"
                          : "bg-[#EAF2FC] text-[#0066CC]"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <p className="flex-1 text-sm font-semibold text-[#1D1D1F]">
                          {notification.title}
                        </p>

                        {!notification.read && (
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0066CC]" />
                        )}
                      </div>

                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[#86868B]">
                        {notification.message}
                      </p>

                      <p className="mt-2 text-[11px] font-medium text-[#0066CC]">
                        {relativeDate(
                          notification.created_at
                        )}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>,
          document.body
        )}
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [aiOpen, setAiOpen] = useState(false);

const [subscription, setSubscription] = useState(null);

useEffect(() => {
  api
    .get("/subscription")
    .then((res) => {
      setSubscription(res.data);
    })
    .catch((error) => {
      console.error("Error cargando suscripción:", error);
    });
}, []);

const PLAN_LEVELS = {
  starter: 1,
  professional: 2,
  business: 3,
};

const currentPlan = subscription?.plan || "trial";

const hasPlanAccess = (item) => {
  if (item.alwaysVisible) {
    return true;
  }

  // Durante la prueba gratuita se puede ver todo.
  if (currentPlan === "trial") {
    return true;
  }

  if (!item.minPlan) {
    return true;
  }

  return (
    (PLAN_LEVELS[currentPlan] || 0) >=
    (PLAN_LEVELS[item.minPlan] || 0)
  );
};

  return (
    <div className="flex min-h-screen bg-[#F5F5F7]">
      <aside className="sticky top-0 flex h-screen w-64 flex-col border-r border-[#D2D2D7]/50 bg-[#F5F5F7] p-4">
        <div className="flex items-center gap-2.5 px-3 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0066CC] shadow-[0_4px_12px_rgba(0,102,204,0.3)]">
            <Wrench
              className="h-5 w-5 text-white"
              strokeWidth={2}
            />
          </div>

          <div className="min-w-0">
            <span className="block font-display text-xl font-bold leading-tight tracking-tight text-[#1D1D1F]">
              Navirox
            </span>

            {user?.org_name && (
              <span
                data-testid="org-name-label"
                className="block truncate text-xs text-[#86868B]"
              >
                {user.org_name}
              </span>
            )}
          </div>
        </div>

<nav className="mt-4 flex flex-1 flex-col gap-1 overflow-y-auto">
  {NAV.filter(
    (item) =>
      (!item.adminOnly || user?.role === "admin") &&
      (!item.roles || item.roles.includes(user?.role)) &&
      hasPlanAccess(item)
  ).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-white text-[#0066CC] shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
                    : "text-[#1D1D1F]/70 hover:bg-white/60 hover:text-[#1D1D1F]"
                }`
              }
            >
              <item.icon
                className="h-[18px] w-[18px]"
                strokeWidth={1.8}
              />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="open-ai-assistant"
          type="button"
          onClick={() => setAiOpen(true)}
          className="mb-3 flex items-center gap-3 rounded-xl bg-gradient-to-br from-[#0066CC] to-[#5856D6] px-3 py-2.5 text-sm font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all duration-200 hover:-translate-y-0.5"
        >
          <Sparkles
            className="h-[18px] w-[18px]"
            strokeWidth={1.8}
          />
          Asistente IA
        </button>

        <div className="flex items-center gap-2 rounded-xl bg-white p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0066CC]/10 text-sm font-semibold text-[#0066CC]">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[#1D1D1F]">
              {user?.name}
            </p>
            <p className="truncate text-xs capitalize text-[#86868B]">
              {user?.role}
            </p>
          </div>

          <NotificationsButton navigate={navigate} />

          <button
            data-testid="logout-btn"
            type="button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[#86868B] transition-colors hover:bg-[#FFEBEA] hover:text-[#FF3B30]"
          >
            <LogOut
              className="h-[18px] w-[18px]"
              strokeWidth={1.8}
            />
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {children}
      </main>

      <AIAssistant
        open={aiOpen}
        onOpenChange={setAiOpen}
      />
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="mb-8 flex items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1D1D1F]">
          {title}
        </h1>

        {subtitle && (
          <p className="mt-1 text-sm text-[#86868B]">
            {subtitle}
          </p>
        )}
      </div>

      {action}
    </div>
  );
}